/**
 * Settle up: the dialog the plan's button opens, and the one place somebody
 * says what was actually paid.
 *
 * The button used to say Record and write the suggestion the instant it was
 * pressed — the whole amount, dated today, no way back but Cancel on the row
 * it had just made. That is a fine thing to do to a plan that is always right,
 * and this one is a suggestion by construction: it says what *would* clear the
 * ledger, not what happened. Somebody paid 100.00 of the 140.00, or paid it on
 * Sunday, and both were things the app made them file and then correct.
 *
 * So the press opens this instead. Two fields, because there are exactly two
 * things the plan cannot know — how much, and when — and everything else about
 * the settlement is already decided: who paid whom is the row it was opened
 * from, and the description, the category and the split come from
 * `settlementDraft`. It opens holding the whole suggested amount, dated today,
 * so the common case is still open-and-confirm; the fields are for the case
 * that used to need an edit afterwards.
 *
 * `src/lib/settle.ts` holds what it says: the sheet, what stops it being
 * written and the sentence about what it would settle, as pure functions a
 * test can pin without a DOM.
 *
 * ## The ways out
 *
 * Cancel, Escape and the scrim, exactly as `SettingsPanel` has them and for
 * the same reason — this is a modal over a screen whose controls are still
 * visible behind it. Nothing is written until Settle up is pressed, so all
 * three ways out are the same harmless thing.
 *
 * The focus goes to Cancel rather than into the amount, which is deliberate:
 * focusing the box raises a phone keyboard over the dialog for a press that
 * is usually just a confirmation of the number already in it.
 *
 * ## Standing still
 *
 * The panel is anchored to the bottom edge of the screen, so it grows upwards:
 * a row that appears below the pad moves the pad, and the pad is the thing
 * with a thumb on it. So nothing in here changes height as the number changes.
 * The shortcut back to the whole amount lives on the label's line, which is
 * kept tall enough for it whether it is showing or not, and the sentence under
 * the date is kept two lines tall whichever sentence it is holding. Both are
 * done in `.settle__ask` and `.settle__note` — the layout is held by the
 * stylesheet, not by rendering something invisible.
 *
 * ## Which project it was for
 *
 * A payment can be tagged to a project, so it is on record what it settled.
 * Opened from a project's own plan, the project is fixed — it is named in the
 * title bar and is not a choice, because the amount suggested is that
 * project's. Opened from the group's plan, `projects` turns on an optional
 * "For project" picker (`ProjectPicker`, open projects only, "No project"
 * first). Either way the tag is a label: the payment moves the group's
 * balances exactly as an untagged one would.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import type { Payment, Project } from '../api'
import { formatMoney } from '../lib/format'
import { projectIdToSend, projectPicker } from '../lib/projects'
import {
  describeSettleUp,
  openSettleUp,
  settleUpProblem,
  wholeAmountLabel,
} from '../lib/settle'
import type { SettleUpSheet } from '../lib/settle'
import { AmountPad } from './AmountPad'
import { ProjectPicker } from './ProjectPicker'

const today = () => new Date().toISOString().slice(0, 10)

export function SettleUp({
  payment,
  currency,
  myId,
  busy,
  project = null,
  projects,
  onSettle,
  onClose,
}: {
  /** The suggested payment the button was pressed on. */
  payment: Payment
  currency: string
  myId: string
  /** A write already going out, which is the one thing that disables Settle up. */
  busy: boolean
  /** Opened from this project's plan: the payment is for it, and that is not a choice. */
  project?: Pick<Project, 'id' | 'name'> | null
  /** Opened from the group's plan: the projects the optional picker offers. */
  projects?: readonly Project[]
  onSettle: (settled: SettleUpSheet, projectId: string | null) => void
  onClose: () => void
}) {
  const uid = useId()
  const cancel = useRef<HTMLButtonElement>(null)
  const [sheet, setSheet] = useState<SettleUpSheet>(() => openSettleUp(payment, today()))
  const [chosenProject, setChosenProject] = useState<string | null>(null)
  // A choice that has stopped being one — archived since the dialog opened —
  // is sent as none, the way the expense sheet treats it, rather than as a
  // tag the server can only refuse.
  const projectId = project
    ? project.id
    : (projectIdToSend({
        chosen: chosenProject,
        initial: null,
        editing: false,
        options: projectPicker(projects ?? [], null).options,
      }) ?? null)

  // Focus into the dialog on the way in, and back onto the button that opened
  // it on the way out — so Escape leaves somebody on a keyboard on the row
  // they were reading rather than at the top of the document.
  useEffect(() => {
    const opener = document.activeElement
    cancel.current?.focus()
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const money = (cents: number) => formatMoney(cents, currency)
  const suggested = money(payment.amountCents)
  const problem = settleUpProblem(sheet, payment, suggested)

  const mine = payment.fromUserId === myId
  const payer = mine ? 'You' : (payment.fromDisplayName ?? payment.fromUserId)
  const payee = payment.toUserId === myId ? 'you' : (payment.toDisplayName ?? payment.toUserId)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (problem !== null || busy) return
    onSettle(sheet, projectId)
  }

  return (
    <div className="settle">
      {/* The third way out, and not a control: it carries no label, because
          Cancel is right there and a nameless button in the tab order is
          worse than no button at all. */}
      <div className="settle__scrim" aria-hidden="true" onClick={onClose} />
      <div
        className="settle__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
      >
        <div className="settle__bar">
          <h2 className="settle__title" id={`${uid}-title`}>
            Settle up
          </h2>
          <span className="settle__who">
            <strong>{payer}</strong> {mine ? 'pay' : 'pays'} <strong>{payee}</strong>
            {project && (
              <>
                {' '}
                for <strong>{project.name}</strong>
              </>
            )}
          </span>
        </div>

        <form className="form settle__form" onSubmit={submit}>
          <div className="form__row">
            {/* The question and its shortcut share a line, and the line keeps
                the shortcut's height whether or not the shortcut is there.
                Below the pad it was a row that appeared the moment somebody
                typed, and this panel is anchored to the bottom edge of the
                screen — so it did not push the note down, it pushed the pad
                up, out from under the thumb typing into it. */}
            <div className="settle__ask">
              <span id={`${uid}-amount-label`} className="sheet__label">
                How much was paid?
              </span>
              {/* Only once the amount has been changed: a button that puts
                  back a number already in the box is a button about nothing.
                  It belongs to the field rather than to the foot of the
                  dialog, which is the other half of why it moved up here —
                  see `wholeAmountLabel` for the words. */}
              {sheet.amountCents !== payment.amountCents && (
                <button
                  type="button"
                  className="button--quiet settle__whole"
                  onClick={() =>
                    setSheet((was) => ({ ...was, amountCents: payment.amountCents }))
                  }
                >
                  {wholeAmountLabel(suggested)}
                </button>
              )}
            </div>
            <AmountPad
              amountCents={sheet.amountCents}
              currency={currency}
              labelledBy={`${uid}-amount-label`}
              onChange={(cents) => setSheet((was) => ({ ...was, amountCents: cents }))}
            />
          </div>

          <div className="form__row form__row--narrow">
            <label htmlFor={`${uid}-date`}>When was it paid?</label>
            <input
              id={`${uid}-date`}
              type="date"
              value={sheet.occurredOn}
              onChange={(event) =>
                setSheet((was) => ({ ...was, occurredOn: event.target.value }))
              }
            />
          </div>

          {!project && projects && (
            <ProjectPicker
              id={`${uid}-project`}
              label="For project"
              projects={projects}
              currentId={null}
              value={chosenProject}
              onChange={setChosenProject}
            />
          )}

          {/*
            What the press would write, said before it is pressed — or what is
            stopping it. Never both: a sheet that cannot be written has
            nothing true to say about what it would settle.
          */}
          {problem === null ? (
            <p className="muted note settle__note">{describeSettleUp(sheet, payment, money)}</p>
          ) : (
            <p className="bad note settle__note">{problem}</p>
          )}

          <p className="actions">
            <button type="submit" disabled={problem !== null || busy}>
              Settle up
            </button>
            <button ref={cancel} type="button" className="button--quiet" onClick={onClose}>
              Cancel
            </button>
          </p>
        </form>
      </div>
    </div>
  )
}
